import SearchEmpty from "@images/empty/search_empty.svg"
import SubscribersEmpty from "@images/empty/subscribers_empty.svg"
import { word } from "@modules/core/utils/i18n"
import useDebounce from "@modules/hooks/useDebounce"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { ProgramInfinite } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOff, ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { ProgramInfoSasPendingSubscribers } from "@modules/programs/programSas/components/programInfoSasPendingSubscribers"
import { SASRequestsToAcceptView } from "@modules/programs/programSas/components/sasRequestsToAcceptView"
import { ProgramSubscriberItem } from "@modules/programs/programSubscriber/components/programSubscriberItem"
import { useProgramSubscriberSearch } from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { getSubscriptionCountText } from "@modules/programs/programUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EmptyView } from "@modules/ui/components/emptyView"
import { HuSearchBar } from "@modules/ui/components/huSearchBar"
import { HUText } from "@modules/ui/components/huText"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { useEffect, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useTheme } from "styled-components"

type ProgramSubscribersListProps = {
  program: ProgramInfinite | ProgramOnOff
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscribersList({ program, className, ...props }: ProgramSubscribersListProps) {
  const defautlClassName = "flex flex-column flex-grow-1 mb-6"
  const [isSASRequestToAcceptViewDisplayed, setIsSASRequestToAcceptViewDisplayed] = useState(false)
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(program)
  const theme = useTheme()
  const { getCurrentStackState } = useSidebar()

  const toggleIsSASRequestToAcceptView = () => {
    setIsSASRequestToAcceptViewDisplayed(!isSASRequestToAcceptViewDisplayed)
  }

  const [searchTerms, setsearchTerms] = useState<string>("")
  const debouncedSearchTerms = useDebounce(searchTerms, 300)
  const {
    data: { content: subscribers, totalSize },
    refetch,
    isLoading: isLoadingSubscribers,
    isFetching,
    fetchNextPage,
  } = useProgramSubscriberSearch(program.id, debouncedSearchTerms)

  const updateIsSASRequestToAcceptViewDisplayed = (newState: boolean) => {
    setIsSASRequestToAcceptViewDisplayed(newState)
    refetch()
  }

  useEffect(() => {
    if (!isLoadingSubscribers) {
      const subscribersListScrollPosition = getCurrentStackState("subscribersListScrollPosition")

      if (subscribersListScrollPosition) {
        const sidebarContent = document.getElementById("sidebar-content")

        if (sidebarContent) {
          sidebarContent.scrollTo({ top: parseInt(subscribersListScrollPosition), behavior: "smooth" })
        }
      }
    }
  }, [isLoadingSubscribers])

  return (
    <div className={className ?? defautlClassName} style={{ marginTop: 130 }} {...props}>
      {!isSASRequestToAcceptViewDisplayed ? (
        <>
          {program.entranceCheckEnabled && (
            <ProgramInfoSasPendingSubscribers
              program={program}
              displaySASRequestToAcceptView={() => updateIsSASRequestToAcceptViewDisplayed(true)}
            />
          )}
          <HUText fontStyle="BXS" color={theme.neutralColor900} className="mb-3">
            {getSubscriptionCountText(
              program.subscriptionCount,
              isAuthor,
              isAcceptedSharedCoach,
              true,
              program._programType,
            ).toUpperCase()}
          </HUText>
          <HuSearchBar
            placeholder={word("program.subscribersList.search.placeholder")}
            searchTerm={searchTerms}
            setSearchTerm={setsearchTerms}
          />

          {isLoadingSubscribers ? <LoadingSpinner /> : null}

          {subscribers.length > 0 ? (
            <InfiniteScroll
              dataLength={subscribers.length}
              next={fetchNextPage}
              hasMore={subscribers.length < (totalSize || 0)}
              scrollableTarget={"sidebar-content"}
              loader={<HUText fontStyle="BM">{word("global.loading")}</HUText>}
            >
              {subscribers.map((subscriber) => (
                <ProgramSubscriberItem
                  program={program}
                  subscriber={subscriber}
                  key={subscriber.id}
                  canSeeSubscriberDetails={true}
                />
              ))}
            </InfiniteScroll>
          ) : !isLoadingSubscribers && !isFetching && searchTerms.length > 0 ? (
            <EmptyView
              image={{ src: SearchEmpty }}
              title={word("explore.search.no_result.title", { searchTerms })}
              description={word("explore.search.no_result.message")}
              flexGrow={false}
              textAlign="left"
              imagePosition="after"
              style={{
                backgroundColor: theme.mainWhite,
                display: "flex w-full p-0",
              }}
            />
          ) : !isLoadingSubscribers && debouncedSearchTerms?.length === 0 ? (
            <EmptyView
              image={{ src: SubscribersEmpty }}
              title={
                program._programType === ProgramOnOffTypeOf
                  ? word("program.onOff.subscribers.title.empty")
                  : word("program.subscribers.title.empty")
              }
              description={word("program.subscribers.description.empty")}
              flexGrow={false}
              className="p-6"
              style={{ backgroundColor: theme.mainWhite }}
            />
          ) : null}
        </>
      ) : (
        <SASRequestsToAcceptView
          program={program}
          toggleIsSASRequestToAcceptView={toggleIsSASRequestToAcceptView}
          hideIsSASRequestToAcceptView={() => updateIsSASRequestToAcceptViewDisplayed(false)}
        />
      )}
    </div>
  )
}
